import { useToast } from '@chakra-ui/react';
import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Node,
  useReactFlow,
  getConnectedEdges,
  Edge,
  XYPosition,
  useStore,
} from 'reactflow';

import { typeguards as commonTypeguards } from '@finley-technologies/common';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { successToastConfig } from 'packages/common-frontend/constants';
import {
  getNewNodeParams,
  GraphContextType,
} from 'packages/tapeBuilder/ui/GraphContext';

// This hook is based on the example found at https://pro.reactflow.dev/examples/react/copy-paste-11
// Use the reactFlow credentials found in 1password to log into the React Flow Pro docs.
export const useCopyPaste = (graphContext: GraphContextType) => {
  const graphContextRef = useRef<GraphContextType>(graphContext);
  const mousePosRef = useRef<XYPosition>({ x: 0, y: 0 });
  const reactFlowDomNode = useStore((state) => state.domNode);
  const toast = useToast();

  useEffect(() => {
    // Here we are storing a new reference to the graphContext object every time
    // it changes. It would be unnecessary to update each useCallback() function
    // below every time graphContext changes, since we can just check the current
    // value of the ref whenever the user decides to copy or paste nodes. This prevents
    // some extra updates and rerenders.
    graphContextRef.current = graphContext;
  }, [graphContext]);

  const { getNodes, setNodes, getEdges, setEdges, project } = useReactFlow();

  const [bufferedNodes, setBufferedNodes] = useState<Node[]>([]);
  const [bufferedEdges, setBufferedEdges] = useState<Edge[]>([]);
  const [bufferedGraphNodes, setBufferedGraphNodes] = useState<
    TapeBuilderUI.TapeBuilderNodeAbstract[]
  >([]);

  useEffect(() => {
    const preventDefault = (e: Event) => e.preventDefault();
    const onMouseMove = (event: MouseEvent) => {
      const bounds = reactFlowDomNode?.getBoundingClientRect();
      mousePosRef.current = {
        x: event.clientX - (bounds?.left ?? 0),
        y: event.clientY - (bounds?.top ?? 0),
      };
    };

    if (reactFlowDomNode) {
      reactFlowDomNode.addEventListener('cut', preventDefault);
      reactFlowDomNode.addEventListener('copy', preventDefault);
      reactFlowDomNode.addEventListener('paste', preventDefault);

      reactFlowDomNode.addEventListener('mousemove', onMouseMove);
    }

    return () => {
      if (reactFlowDomNode) {
        reactFlowDomNode.removeEventListener('cut', preventDefault);
        reactFlowDomNode.removeEventListener('copy', preventDefault);
        reactFlowDomNode.removeEventListener('paste', preventDefault);

        reactFlowDomNode.removeEventListener('mousemove', onMouseMove);
      }
    };
  }, [reactFlowDomNode]);

  const copy = useCallback(() => {
    const selectedNodes = getNodes().filter(
      (node) => node.selected && !node.hidden,
    );
    const selectedEdges = getConnectedEdges(selectedNodes, getEdges()).filter(
      // Only copy edges that are connected to nodes on both ends.
      (edge) => {
        const isExternalSource = selectedNodes.every(
          (n) => n.id !== edge.source,
        );
        const isExternalTarget = selectedNodes.every(
          (n) => n.id !== edge.target,
        );

        return !(isExternalSource || isExternalTarget);
      },
    );

    const selectedGraphNodes = selectedNodes
      .map((node) =>
        graphContextRef.current.state.nodes.find(
          (n) => n.metadata.tapeBuilderNodeId === node.id,
        ),
      )
      .filter(commonTypeguards.isNotNullOrUndefined);

    if (selectedNodes.length === 0) {
      return;
    }

    setBufferedNodes(selectedNodes);
    setBufferedEdges(selectedEdges);
    setBufferedGraphNodes(selectedGraphNodes);

    const nodeCount = selectedGraphNodes.length;
    const toastConfig = { ...successToastConfig };
    toastConfig.title = `Copied ${nodeCount} ${
      nodeCount > 1 ? 'nodes' : 'node'
    }`;

    // Notify the user
    toast(toastConfig);
  }, [getNodes, getEdges, toast]);

  const paste = useCallback(() => {
    const { x: pasteX, y: pasteY } = project({
      x: mousePosRef.current.x,
      y: mousePosRef.current.y,
    });

    // Here we get the coordinates for the most upper-left node so that we can
    // correctly position the new nodes relative to the current mouse position
    const minX = Math.min(...bufferedNodes.map((s) => s.position.x));
    const minY = Math.min(...bufferedNodes.map((s) => s.position.y));

    // This maps old node IDs to new node IDs, so we can preserve
    // edge connections in the newly pasted nodes.
    const oldIdToNew: { [key: string]: string } = {};

    const newGraphNodes = bufferedGraphNodes.map((node) => {
      const nodeType = node.metadata.tapeBuilderNodeType;
      const nodeInterface = TapeBuilderUI.getUINodeByType(nodeType);
      const newNode = nodeInterface.createNewNode({
        ...getNewNodeParams(graphContextRef.current),
        nodeToCopy: node, // Must pass old node to copy its config
      });

      oldIdToNew[node.metadata.tapeBuilderNodeId] =
        newNode.metadata.tapeBuilderNodeId;

      return newNode;
    });

    const newNodes: Node[] = bufferedNodes.map((node) => {
      const id = oldIdToNew[node.id];
      const newX = pasteX + (node.position.x - minX);
      const newY = pasteY + (node.position.y - minY);

      return { ...node, id, position: { x: newX, y: newY }, data: id };
    });

    const newEdges: Edge[] = bufferedEdges.map((edge) => {
      const source = oldIdToNew[edge.source];
      const target = oldIdToNew[edge.target];
      const id = `${source}:${target}`;

      return { ...edge, id, source, target };
    });

    newGraphNodes.forEach((node) =>
      graphContextRef.current.dispatch({ type: 'addNode', node }),
    );
    newEdges.forEach((edge) =>
      graphContextRef.current.dispatch({ type: 'addEdge', edge }),
    );
    setNodes((nodes) => [
      ...nodes.map((node) => ({ ...node, selected: false })),
      ...newNodes,
    ]);
    setEdges((edges) => [
      ...edges.map((edge) => ({ ...edge, selected: false })),
      ...newEdges,
    ]);
  }, [
    bufferedNodes,
    bufferedEdges,
    project,
    setNodes,
    setEdges,
    bufferedGraphNodes,
  ]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (graphContextRef.current.state.mode !== 'Edit') {
        // Disable copy/paste unless in edit mode
        return;
      }

      // metaKey is 'command' on macs
      const controlKey = navigator.userAgent.includes('Macintosh')
        ? e.metaKey
        : e.ctrlKey;

      if (controlKey && e.key === 'c') {
        copy();
      }

      if (controlKey && e.key === 'v') {
        paste();
      }
    }

    // Binding the listener to the reactFlowDowNode and not the document so that
    // we don't capture ctrl-c or ctrl-v when the user copies or pastes JSON in the
    // text editor.
    reactFlowDomNode?.parentElement?.addEventListener('keydown', onKeyDown);
    return () => {
      reactFlowDomNode?.parentElement?.removeEventListener(
        'keydown',
        onKeyDown,
      );
    };
  }, [copy, paste, reactFlowDomNode]);

  return { copy, paste, bufferedNodes, bufferedEdges };
};
